<template>
	<Teleport to="body">
		<Transition name="portal-drawer">
			<div v-if="open" class="portal-drawer" @keydown.esc="emit('close')" @keydown.tab="trapFocus">
				<div class="portal-drawer__overlay" @click="emit('close')" />
				<div
					id="portal-menu"
					ref="panel"
					class="portal-drawer__panel"
					role="dialog"
					aria-modal="true"
					aria-label="Menu"
					tabindex="-1"
				>
					<div class="portal-drawer__top">
						<PortalProfile tone="default" />
						<PortalIconButton label="Close menu" tone="light" @click="emit('close')">
							<Icon type="ds_close" size="--size-16" />
						</PortalIconButton>
					</div>
					<PortalNav :items="items" :active-id="activeId" @navigate="emit('close')" />
					<PortalAppPromotion class="portal-drawer__promotion" variant="stores" />
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { Icon } from '@simple-life-app/growth-ui-kova'
import type { PortalNavItem, PortalSectionId } from '@/portal/sections'
import PortalAppPromotion from './PortalAppPromotion.vue'
import PortalIconButton from './PortalIconButton.vue'
import PortalNav from './PortalNav.vue'
import PortalProfile from './PortalProfile.vue'

/**
 * The mobile side menu (Figma 14254:175875 "Sidebar" over an overlay): the customer at the top, the
 * sections, and the app promotion with the store buttons at the bottom. Teleported to `<body>` like the
 * DS Modal, so no containing block inside the shell (LayoutScreen / LayoutGrid set `container-type`)
 * can trap its fixed position.
 *
 * Closes on a pick in the list (also the section already open), the overlay, the close button, Escape and —
 * through the parent — any route change. A dialog for assistive tech: it takes the focus when it opens and
 * keeps Tab inside until it closes.
 */

const props = defineProps<{
	open: boolean
	items: PortalNavItem[]
	activeId: PortalSectionId | null
}>()

const emit = defineEmits<{ close: [] }>()

const panel = ref<HTMLElement | null>(null)

watch(
	() => props.open,
	async (open) => {
		if (!open) return
		await nextTick()
		panel.value?.focus()
	},
)

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

function trapFocus(event: KeyboardEvent) {
	const focusable = panel.value ? Array.from(panel.value.querySelectorAll<HTMLElement>(FOCUSABLE)) : []
	if (!focusable.length) return
	const first = focusable[0]
	const last = focusable[focusable.length - 1]
	const active = document.activeElement
	if (event.shiftKey && (active === first || active === panel.value)) {
		event.preventDefault()
		last.focus()
	} else if (!event.shiftKey && active === last) {
		event.preventDefault()
		first.focus()
	}
}
</script>

<style scoped>
/* Same layer as the DS Modal (z-index 1000), which also teleports to <body>. */
.portal-drawer {
	position: fixed;
	inset: 0;
	z-index: 1000;
}

.portal-drawer__overlay {
	position: absolute;
	inset: 0;
	background: var(--overlay);
}

/*
 * Figma 14254:175875: 334 of 390 wide, the bottom corners 40, the frame's drop shadow (0 0 40 at 10% black — no
 * token for shadows); scrolls on its own on a short screen.
 */
.portal-drawer__panel {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	display: flex;
	flex-direction: column;
	gap: var(--size-32);
	width: min(334px, 86%);
	padding: var(--size-16) var(--size-16) calc(var(--size-16) + env(safe-area-inset-bottom));
	overflow-y: auto;
	overscroll-behavior: contain;
	border-radius: 0 0 var(--size-40) var(--size-40);
	background: var(--background-base);
	box-shadow: 0 0 40px rgba(0, 0, 0, 0.1);
	outline: none;
}

.portal-drawer__top {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-12);
	min-height: var(--size-40);
}

.portal-drawer__promotion {
	margin-top: auto;
}

.portal-drawer-enter-active,
.portal-drawer-leave-active {
	transition: opacity 200ms ease;
}

.portal-drawer-enter-active .portal-drawer__panel,
.portal-drawer-leave-active .portal-drawer__panel {
	transition: transform 200ms ease;
}

.portal-drawer-enter-from,
.portal-drawer-leave-to {
	opacity: 0;
}

.portal-drawer-enter-from .portal-drawer__panel,
.portal-drawer-leave-to .portal-drawer__panel {
	transform: translateX(-100%);
}

@media (prefers-reduced-motion: reduce) {
	.portal-drawer-enter-active,
	.portal-drawer-leave-active,
	.portal-drawer-enter-active .portal-drawer__panel,
	.portal-drawer-leave-active .portal-drawer__panel {
		transition: none;
	}
}
</style>
